import {
  useMutation,
  type UseMutationOptions,
  useQuery,
} from '@tanstack/react-query';
import { register } from '../../api/Endpoints';
import {
  AddressValidationResponse,
  InfoPostData,
  ConfirmationResponse,
  ConsentPostData,
  ConsentResponse,
  CountriesResponse,
  KitInfoResponse,
  SignUpRequestWithKit,
  RegisterResponse,
  StateResponse,
  ValidateEmailData,
  ValidateEmailResponse,
  Address,
  MultiKitValidationData,
  MultiKitValidationResponse,
} from './Register.model';

export const useRegisterKitQuery = (kitId: string) => {
  return useQuery<KitInfoResponse>({
    queryKey: ['kit-barcode', kitId],
    queryFn: async () => {
      const response = await register.getKitInfo(kitId);
      return response?.data;
    },
    enabled: false,
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });
};

export const useAccountCreationMutation = (
  options?: Partial<
    UseMutationOptions<ValidateEmailResponse, Error, ValidateEmailData>
  >,
) => {
  return useMutation({
    ...options,
    mutationFn: async (data: ValidateEmailData) => {
      const res = await register.validateEmail(data);
      return res.data;
    },
  });
};

export const useCountriesQuery = () => {
  return useQuery<CountriesResponse>({
    queryKey: ['countries'],
    queryFn: async () => {
      const response = await register.getCountries();
      return response.data;
    },
  });
};

export const useStatesQuery = (selectedState?: string) => {
  return useQuery<StateResponse>({
    queryKey: ['state', selectedState],
    queryFn: async () => {
      const response = await register.getStates(selectedState);
      return response.data;
    },
    enabled: !!selectedState,
  });
};

export const useAddressValidationMutation = (
  options?: Partial<
    UseMutationOptions<AddressValidationResponse, Error, Address>
  >,
) => {
  return useMutation({
    ...options,
    mutationFn: async (formData: Address) => {
      const res = await register.validateAddress(formData);
      return res.data;
    },
  });
};

export const useConfirmDetailsMutation = (
  options?: Partial<
    UseMutationOptions<ConfirmationResponse, Error, InfoPostData>
  >,
) => {
  return useMutation({
    ...options,
    mutationFn: async (formData: InfoPostData) => {
      const response = await register.confirmDetails(formData);
      return response.data;
    },
  });
};

export const useUserConsentMutation = (
  options?: Partial<
    UseMutationOptions<ConsentResponse, Error, ConsentPostData>
  >,
) => {
  return useMutation({
    ...options,
    mutationFn: async (formData: ConsentPostData) => {
      const response = await register.submitConsent(formData);
      return response.data;
    },
  });
};

export const useRegisterKitMutation = (
  options?: Partial<
    UseMutationOptions<RegisterResponse, Error, SignUpRequestWithKit>
  >,
) => {
  return useMutation({
    ...options,
    mutationFn: async (formData: SignUpRequestWithKit) => {
      const response = await register.registerKit(formData);
      return response.data;
    },
  });
};

export const useMultiKitValidationKitMutation = (
  options?: Partial<
    UseMutationOptions<MultiKitValidationResponse, Error, MultiKitValidationData>
  >,
) => {
  return useMutation({
    ...options,
    mutationFn: async (formData: MultiKitValidationData) => {
      const response = await register.validateMultiKit(formData);
      return response.data;
    },
  });
};

export const useRegisterMultiKitMutation = (
    options?: Partial<
        UseMutationOptions<RegisterResponse, Error, SignUpRequestWithKit>
    >,
) => {
  return useMutation({
    ...options,
    mutationFn: async (formData: SignUpRequestWithKit) => {
      const response = await register.registerMultiKit(formData);
      return response.data;
    },
  });
};
